'use client';

import { useMemo } from 'react';
import { ChainType, chainSettings, defaultNativeAddress } from '@/constants/chain-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';

/**
 * Hook to handle native currency conversion to wrapped version for V2/V3 DEXs
 */
export function useNativeTokenHandler(chainId: ChainType) {
  const chainConfig = chainSettings[chainId as ChainType];

  /**
   * Check if a token is the native currency
   */
  const isNativeToken = (token: TokenConfigType | undefined): boolean => {
    if (!token) return false;
    return token.address.toLowerCase() === ("0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE").toLowerCase() ||
      token.address.toLowerCase() === ("0x0000000000000000000000000000000000000000").toLowerCase() || token.symbol.toLowerCase() === chainConfig?.nativeCurrency?.symbol.toLowerCase();
  };

  /**
   * Check if a token is a wrapped native token
   * Uses DEX-specific weth if available, otherwise chain's default wNative
   */
  const isWrappedNative = (token: TokenConfigType | undefined, dexSlug?: string): boolean => {
    if (!token?.address) return false;

    const tokenAddress = token.address.toLowerCase();

    // Check DEX-specific wrapped native first
    if (dexSlug) {
      const dexConfig = DEX_LIST[dexSlug];
      if (dexConfig?.weth?.address) {
        if (tokenAddress === dexConfig.weth.address.toLowerCase()) {
          return true;
        }
      }
    }

    // Fall back to chain's default wrapped native
    if (chainConfig?.wNative?.address) {
      if (tokenAddress === chainConfig.wNative.address.toLowerCase()) {
        return true;
      }
    }

    return false;
  };

  /**
   * Get the wrapped native token address for a specific DEX
   * Returns DEX-specific weth if available, otherwise chain's default wNative
   */
  const getWrappedNativeAddress = (dexSlug?: string): string | undefined => {
    if (dexSlug) {
      const dexConfig = DEX_LIST[dexSlug];
      if (dexConfig?.weth?.address) {
        return dexConfig.weth.address;
      }
    }
    return chainConfig?.wNative?.address;
  };

  /**
   * Check if a DEX requires wrapped tokens (V2/V3/Velodrome DEXs)
   */
  const dexRequiresWrapped = (dexSlug: string): boolean => {
    const dex = DEX_LIST[dexSlug];
    if (!dex) return false;
    
    // V2, V3, and velodrome-type DEXs require wrapped tokens
    return dex.type === 'v2' || dex.type === 'v3' || dex.type === 'velodrome';
  };

  /**
   * Convert native token to wrapped version if needed
   * Uses DEX-specific wrapped token if available, otherwise falls back to chain's default wNative
   */
  const getEffectiveToken = (
    token: TokenConfigType | undefined,
    dexSlug: string
  ): TokenConfigType | undefined => {
    if (!token?.address) return token;

    const dexConfig = DEX_LIST[dexSlug];

    // If it's native currency and DEX requires wrapped, return wrapped version
    if (isNativeToken(token) && dexRequiresWrapped(dexSlug)) {
      // Use DEX-specific wrapped token if available
      if (dexConfig?.weth) {
        return {
          address: dexConfig.weth.address,
          decimals: dexConfig.weth.decimals,
          symbol: dexConfig.weth.symbol,
          name: dexConfig.weth.name,
          logo: dexConfig.weth.logo || token.logo,
        };
      }

      // Fall back to chain's default wrapped native
      if (chainConfig?.wNative?.address) {
        return {
          address: chainConfig.wNative.address,
          decimals: chainConfig.wNative.decimals,
          symbol: chainConfig.wNative.symbol,
          name: chainConfig.wNative.name,
          logo: chainConfig.wNative.logo || token.logo,
        };
      }
    }

    return token;
  };

  /**
   * Get wrapped token configuration for the current chain
   */
  const getWrappedToken = (): TokenConfigType | undefined => {
    if (!chainConfig?.wNative) return undefined;

    return {
      address: chainConfig.wNative.address,
      decimals: chainConfig.wNative.decimals,
      symbol: chainConfig.wNative.symbol,
      name: chainConfig.wNative.name,
      logo: chainConfig.wNative.logo || chainConfig.nativeCurrency?.logo,
    };
  };

  /**
   * Get native token configuration for the current chain
   */
  const getNativeToken = (): TokenConfigType | undefined => {
    if (!chainConfig?.nativeCurrency) return undefined;

    return {
      address: defaultNativeAddress,
      decimals: chainConfig.nativeCurrency.decimals,
      symbol: chainConfig.nativeCurrency.symbol,
      name: chainConfig.nativeCurrency.name,
      logo: chainConfig.nativeCurrency.logo,
    };
  };

  /**
   * Check if user needs to wrap/unwrap tokens for the transaction
   */
  const needsWrapping = (
    token0: TokenConfigType | undefined,
    token1: TokenConfigType | undefined,
    dexSlug: string
  ): {
    token0NeedsWrapping: boolean;
    token1NeedsWrapping: boolean;
    hasWrapping: boolean;
  } => {
    const token0Needs = !!(token0 && isNativeToken(token0) && dexRequiresWrapped(dexSlug));
    const token1Needs = !!(token1 && isNativeToken(token1) && dexRequiresWrapped(dexSlug));

    return {
      token0NeedsWrapping: token0Needs,
      token1NeedsWrapping: token1Needs,
      hasWrapping: token0Needs || token1Needs,
    };
  };

  /**
   * Get display information for UI showing native -> wrapped conversion
   */
  const getDisplayInfo = (
    token: TokenConfigType | undefined,
    dexSlug: string
  ): {
    originalToken: TokenConfigType | undefined;
    effectiveToken: TokenConfigType | undefined;
    showsWrapping: boolean;
  } => {
    const effectiveToken = getEffectiveToken(token, dexSlug);
    const showsWrapping = !!(token && isNativeToken(token) && dexRequiresWrapped(dexSlug));

    return {
      originalToken: token,
      effectiveToken,
      showsWrapping,
    };
  };

  return {
    isNativeToken,
    isWrappedNative,
    getWrappedNativeAddress,
    dexRequiresWrapped,
    getEffectiveToken,
    getWrappedToken,
    getNativeToken,
    needsWrapping,
    getDisplayInfo,
    chainConfig,
  };
}















